import { useMutation } from '@tanstack/react-query';
import { api } from '../../api/client';

/**
 * Opens a tool as the signed-in user.
 *
 * Launching is a request, not a link: the platform mints a short-lived
 * credential that tells the tool who is arriving, and hands back the address
 * to go to. A plain href could not do this, because the address is different
 * every time and only means anything for a few hours.
 *
 * Two details that look like superstition but are not:
 *
 * The tab is opened synchronously, inside the click, and pointed somewhere
 * only once the request comes back. A `window.open` after an `await` has left
 * the user-gesture context, and browsers treat it as an unsolicited popup.
 *
 * `opener` is cleared before the tab navigates. Without that, the tool could
 * reach back through `window.opener` into the page that launched it, which is
 * a different application on a different host and has no business being
 * reachable that way.
 */
export function useLaunchTool() {
  return useMutation({
    mutationFn: async (key: string) => {
      const tab = window.open('', '_blank');
      try {
        const { url } = await api.launchTool(key);
        if (tab) {
          tab.opener = null;
          tab.location.replace(url);
        } else {
          // The popup was blocked. Going there in this tab is better than
          // appearing to do nothing.
          window.location.assign(url);
        }
      } catch (error) {
        tab?.close();
        throw error;
      }
    },
  });
}
